import type { AlgoliaSearchHelper } from "algoliasearch-helper";
import { RANGE_META } from "@/services/algoliaRouting.core";
import {
    curatedSearchConfig,
    rangeFiltersState,
} from "@/services/algoliaRouting.blade";

function countRangeFilters(): number {
    let count = 0;

    if (rangeFiltersState.value.specification?.trim()) {
        count++;
    }

    for (const { attribute } of RANGE_META) {
        const range = rangeFiltersState.value[attribute];
        if (
            range &&
            (range.min !== undefined || range.max !== undefined)
        ) {
            count++;
        }
    }

    return count;
}

function countRangeFiltersExcludingCurated(): number {
    const curated = curatedSearchConfig.value.filters;
    let count = 0;

    for (const { attribute, minKey, maxKey } of RANGE_META) {
        const range = rangeFiltersState.value[attribute];
        if (!range || (range.min === undefined && range.max === undefined)) {
            continue;
        }

        const curatedMin =
            minKey && curated
                ? (curated[minKey] as number | undefined)
                : undefined;
        const curatedMax = curated
            ? (curated[maxKey] as number | undefined)
            : undefined;
        const differsFromCurated =
            range.min !== curatedMin || range.max !== curatedMax;

        if (differsFromCurated) count++;
    }

    return count;
}

function countHelperRefinements(helper: AlgoliaSearchHelper): number {
    const state = helper.state;
    let count = 0;

    const addFacetRefinements = (
        refinements: Record<string, string[]> | undefined,
    ) => {
        if (!refinements) return;
        for (const values of Object.values(refinements)) {
            count += values.length;
        }
    };

    addFacetRefinements(state.facetsRefinements);
    addFacetRefinements(state.disjunctiveFacetsRefinements);

    if (state.hierarchicalFacetsRefinements) {
        for (const values of Object.values(state.hierarchicalFacetsRefinements)) {
            count += values.length;
        }
    }

    return count;
}

/** Landing / curated defaults (make, model, etc.) applied via configure.filters. */
export function countCuratedBaselineFilters(): number {
    const config = curatedSearchConfig.value;
    if (!config.isActive || !config.filters) return 0;

    const filters = config.filters;
    let count = 0;

    if (filters.menu) {
        count += Object.values(filters.menu).filter(
            (value) => value != null && value !== "",
        ).length;
    }

    if (filters.refinementList) {
        for (const values of Object.values(filters.refinementList)) {
            count += values.length;
        }
    }

    for (const { minKey, maxKey } of RANGE_META) {
        const hasCuratedRange =
            (minKey && filters[minKey] !== undefined) ||
            filters[maxKey] !== undefined;
        if (hasCuratedRange) count++;
    }

    return count;
}

/** Active filter pills rendered by InstantSearch + range/spec widgets. */
export function countVisibleActiveFilterPills(
    root?: ParentNode,
): number {
    const scope =
        root ?? (typeof document !== "undefined" ? document : null);
    if (!scope) return 0;

    return scope.querySelectorAll(
        "#current-refinements .current-refinements__item, #range-filter-pills .current-refinements__item",
    ).length;
}

export function countActiveMarketplaceFilters(
    helper?: AlgoliaSearchHelper | null,
    root?: ParentNode,
): number {
    const baseline = countCuratedBaselineFilters();
    const visiblePills = countVisibleActiveFilterPills(root);

    if (visiblePills > 0) {
        return baseline + visiblePills;
    }

    const helperExtra = helper ? countHelperRefinements(helper) : 0;
    const rangeExtra = countRangeFiltersExcludingCurated();
    const specExtra = rangeFiltersState.value.specification?.trim() ? 1 : 0;

    return baseline + helperExtra + rangeExtra + specExtra;
}

export function formatFilterCountSuffix(count: number): string {
    return count > 0 ? ` (${count})` : "";
}

export function updateFilterToggleLabels(
    count: number,
    root: ParentNode = document,
): void {
    const suffix = formatFilterCountSuffix(count);

    root.querySelectorAll<HTMLElement>("[data-filter-label]").forEach((el) => {
        const base = el.dataset.filterLabel ?? el.textContent?.trim() ?? "";
        el.textContent = base + suffix;
    });
}
